import * as React from "react";
import { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import { SMSGateway } from "./OBRSettings";
import { Cache } from "./Account";

export interface GlobalSettings {
  autoApproveReviews: boolean;
  useSSL: boolean;
  showDueDateForItems: boolean;
  sharingEnabled: boolean;
  useForgotPasswordLink: boolean;
  timeTillTimeout: number;
  pacUrl: string;
  useJira: boolean;
  jiraId: string;
  hidePersonalInformation: boolean;
  reindexReadingLevel: boolean;
  reindexSerials: boolean;
  reloadSeries: boolean;
  reloadCategories: boolean;
  apiIpList: string;
}

export interface HostInformationView {
  id: number;
  usePin: boolean;
  useAltId: boolean;
  useLastName: boolean;
  usePhone: boolean;
  useZipcode: boolean;
  pinEditable: boolean;
  emailEditable: boolean;
  addressEditable: boolean;
  phoneNumberEditable: boolean;
  databaseServerAddress: string;
  databaseSid: string;
  databasePort: number;
  patronServiceAddress: string;
  bibServiceAddress: string;
  carlAuthServiceAddress: string;
  displayName: string;
  className: string;
}

export interface ContentServerSettings {
  contentServerAddress: string;
  contentServerSupportsMetadata: boolean;
  contentServerCustomerId: string;
  enableSslCuratedContentServer: boolean;
}

export interface GlobalConfig {
  globalSettings: GlobalSettings;
  googleOauthEnabled: boolean;
  ldapEnabled: boolean;
  obrEnabled: boolean;
  adfsSettings: ADFSSettings;
  publicHostInformation: HostInformationView;
  smsGateways: SMSGateway[];
  allowBorrowersToCancelArrivedHolds: boolean;
  adfsEnabled: boolean;
  carlXWebEcommerceSettings: CarlXWebEcommerceSettings;
  contentServerSettings: ContentServerSettings;
}

export interface CarlXWebEcommerceSettings {
  enabled: boolean;
  url: string;
  buttonText: string;
}

export type GlobalConfigFieldsResponse = [
  GlobalSettings, // "globalSettings"
  boolean, // "googleOauthEnabled"
  boolean, // "ldapEnabled"
  boolean, // obrEnabled
  ADFSSettings, // "adfsSettings"
  HostInformationView,
  SMSGateway[],
  boolean, // allowBorrowersToCancelArrivedHolds
  boolean, // adfsEnabled
  CarlXWebEcommerceSettings,
  ContentServerSettings,
];

export interface ADFSSettings {
  id: number;
  enabled: boolean;
  timeoutUrl: string;
  logoutUrl: string;
  loginUrl: string;
  fedMeta: string;
  useRedirect: boolean;
}

class GlobalConfigCache extends Cache<GlobalConfig> {
  constructor(data: GlobalConfig) {
    super(data);
  }
  protected override equals(data: GlobalConfig): boolean {
    if (this.current === null || data === null) {
      return this.current === data;
    }
    try {
      return JSON.stringify(this.current) === JSON.stringify(data);
    } catch (e: unknown) {
      return false;
    }
  }
}

export const GlobalConfigHolder = (() => {
  const globalConfig = new GlobalConfigCache(null);
  const subscribers: ((event: GlobalConfig) => void)[] = [];
  const fields = [
    "globalSettings",
    "googleOauthEnabled",
    "ldapEnabled",
    "obrEnabled",
    "adfsSettings",
    "publicHostInformation",
    "smsGateways",
    "allowBorrowersToCancelArrivedHolds",
    "adfsEnabled",
    "carlXWebEcommerceSettings",
    "contentServerSettings",
  ];

  const subscribe = (subscriber: (event: GlobalConfig) => void) => {
    subscribers.push(subscriber);

    if (globalConfig.current && globalConfig.valid) {
      subscriber({ ...globalConfig.current });
    } else {
      fetchGlobalConfig();
    }

    // Return an unsubscribe function
    return () => {
      const index = subscribers.indexOf(subscriber);
      if (index !== -1) {
        subscribers.splice(index, 1); // Remove the subscriber when unsubscribed
      }
    };
  };

  let pendingGetGlobalConfig: Promise<GlobalConfig>;
  const fetchGlobalConfig = async () => {
    if (pendingGetGlobalConfig) {
      return pendingGetGlobalConfig;
    }

    if (!globalConfig.valid) {
      pendingGetGlobalConfig = axios
        .post<GlobalConfigFieldsResponse>("/globalConfiguration/fields", fields)
        .then((response) => {
          const [
            globalSettings,
            googleOauthEnabled,
            ldapEnabled,
            obrEnabled,
            adfsSettings,
            publicHostInformation,
            smsGateways,
            allowBorrowersToCancelArrivedHolds,
            adfsEnabled,
            carlXWebEcommerceSettings,
            contentServerSettings,
          ] = response.data;
          globalConfig.set({
            globalSettings,
            googleOauthEnabled,
            ldapEnabled,
            obrEnabled,
            adfsSettings,
            publicHostInformation,
            smsGateways,
            allowBorrowersToCancelArrivedHolds,
            adfsEnabled,
            carlXWebEcommerceSettings,
            contentServerSettings,
          });
          subscribers.forEach((sub) => sub({ ...globalConfig.current }));
          return { ...globalConfig.current };
        })
        .finally(() => {
          pendingGetGlobalConfig = null;
        });

      return pendingGetGlobalConfig;
    }

    return { ...globalConfig.current };
  };

  return {
    current: () => globalConfig.current,
    fetchGlobalConfig,
    subscribe,
  };
})();

export const GlobalConfigContext = createContext<GlobalConfig>(null);

export const GlobalConfigProvider = ({ children }: React.PropsWithChildren) => {
  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>(
    GlobalConfigHolder.current(),
  );

  useEffect(() => {
    const unsubscribe = GlobalConfigHolder.subscribe((data) => {
      setGlobalConfig(data);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <GlobalConfigContext.Provider value={globalConfig}>
      {children}
    </GlobalConfigContext.Provider>
  );
};

export function useGlobalConfig() {
  const context = useContext(GlobalConfigContext);

  if (!context) {
    throw new Error(
      "useGlobalConfig must be used within a GlobalConfigProvider",
    );
  }

  return context;
}
